import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { APIENDPOINT } from '@core/api-url.enum';
import { ApiCacheService } from '@core/cache/api-cache.service';
import { AlertModalComponent } from '@components/alert-modal/alert-modal.component';
import { CommonService } from '@modules/shared/services/common.service';
import type { Button } from '@modules/dynamic-form-builder/model/formBuilderInterface';
import {
  getLoyaltyPointsPackageLabel,
  LOYALTY_POINTS_TEXT
} from '@wallet/component/loyalty-points/loyalty-points.constants';
import { POST_PRICE_LOYALTY } from '@modules/dashboard/components/new-post/newposts/constants';
import {
  PURCHASE_POINTS_MODAL_TEXT,
  PURCHASE_POINTS_OPTION_BUTTON
} from '@wallet/component/loyalty-points/purchase-points-modal/purchase-points-modal.constants';
import type {
  LoyaltyPointsBundleId,
  LoyaltyPointsPackage,
  LoyaltyPointsPackagesResponse
} from '@wallet/component/loyalty-points/loyalty-points.interface';
import { Subject, takeUntil } from 'rxjs';
import { Application } from '@enums/application.enums';

@Component({
  selector: 'app-purchase-points-modal',
  templateUrl: './purchase-points-modal.component.html',
  styleUrls: ['./purchase-points-modal.component.scss']
})
export class PurchasePointsModalComponent implements OnDestroy {
  @Input() creatorId = '';
  @Input() isSubmitting = false;
  @Input() selectedBundleId: LoyaltyPointsBundleId | null = null;

  @Output() packageSelected = new EventEmitter<LoyaltyPointsPackage>();

  @ViewChild('purchasePointsModalRef')
  purchasePointsModalRef?: AlertModalComponent;

  readonly LOYALTY_POINTS_TEXT = LOYALTY_POINTS_TEXT;
  readonly PURCHASE_POINTS_MODAL_TEXT = PURCHASE_POINTS_MODAL_TEXT;
  readonly neutralCoinIconSrc = POST_PRICE_LOYALTY.POINT_COIN_ICON_PATH;
  Application = Application;
  isLoading = false;
  packages: LoyaltyPointsPackage[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly apiCacheService: ApiCacheService,
    public readonly cs: CommonService
  ) {}

  openModal(): void {
    this.purchasePointsModalRef?.openModal();
    this.loadPackages();
  }

  closeModal(): void {
    this.purchasePointsModalRef?.closeModal();
  }

  handlePackageSelection(loyaltyPointsPackage: LoyaltyPointsPackage): void {
    if (this.isSubmitting) {
      return;
    }

    this.packageSelected.emit(loyaltyPointsPackage);
  }

  getPackageButton(loyaltyPointsPackage: LoyaltyPointsPackage): Button {
    return {
      ...PURCHASE_POINTS_OPTION_BUTTON,
      name: loyaltyPointsPackage.bundle_id,
      loading: this.isSubmitting && this.selectedBundleId === loyaltyPointsPackage.bundle_id,
      disabled: this.isSubmitting
    };
  }

  getPackageLabel(loyaltyPointsPackage: LoyaltyPointsPackage): string {
    return getLoyaltyPointsPackageLabel(loyaltyPointsPackage);
  }

  trackByPackage(index: number, loyaltyPointsPackage: LoyaltyPointsPackage): string {
    return `${loyaltyPointsPackage.bundle_id}-${index}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPackages(): void {
    if (!this.creatorId) {
      return;
    }

    this.isLoading = true;

    this.apiCacheService
      .get(APIENDPOINT.loyaltyPointsPackages(this.creatorId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: LoyaltyPointsPackagesResponse) => {
          if (response.status) {
            this.packages = response.data.is_active
              ? response.data.bundles.filter((loyaltyPointsPackage) => loyaltyPointsPackage.is_active)
              : [];
          }
          this.isLoading = false;
        },
        error: (err: Error) => {
          this.cs.showErrorMessage(err.message);
          this.isLoading = false;
        }
      });
  }
}
